import * as THREE from "three";

/**
 * Seeded projectile system for Hailstorm. Deterministic from the run
 * seed — replay validation can re-derive the schedule and compare.
 *
 * MVP scope: linear-trajectory projectiles entering the 40×40 arena
 * from random perimeter points, aimed at the ship's then-current
 * position (snapshot at spawn time). Density ramps via a smoothstep
 * from 1.5 → 8 spawns/sec across the first 120 s.
 */

const ARENA_HALF = 20;          // arena spans -20..+20 on x and y
const CULL_HALF = 30;           // outside ±30 → expire
const DODGE_RADIUS = 4.0;       // ≤ this counts as a dodge
const HIT_RADIUS = 0.9;
const SPEED_MIN = 12;
const SPEED_MAX = 22;

export interface Projectile {
  idx: number;
  origin: THREE.Vector2;
  dir: THREE.Vector2;
  speed: number;
  spawnT: number;
  // Live state
  pos: THREE.Vector2;
  mesh: THREE.Mesh;
  // Per-projectile nearest-approach to ship; tracked for dodge classification
  // at the moment the projectile exits the arena.
  nearestApproach: number;
  hitFlagged: boolean;
}

export interface DodgeEvent {
  /** Projectile index. */
  idx: number;
  /** Closest approach in world units. */
  distance: number;
  /** Bucket weight applied to score. */
  bucket: 1 | 2 | 3;
}

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function smoothstep(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
}

export class Projectiles {
  readonly group = new THREE.Group();
  private active: Projectile[] = [];
  private rng: () => number;
  private nextIdx = 0;
  private nextSpawnT = 0.5;
  /** Aggregate dodge buckets — read by Hailstorm on game over. */
  dodgeCounts = { tight: 0, close: 0, clear: 0 };

  constructor(seed: number) {
    this.rng = makeRng(seed);
  }

  reset(seed: number) {
    this.rng = makeRng(seed);
    this.nextIdx = 0;
    this.nextSpawnT = 0.5;
    this.dodgeCounts = { tight: 0, close: 0, clear: 0 };
    for (const p of this.active) {
      this.group.remove(p.mesh);
      p.mesh.geometry.dispose();
      (p.mesh.material as THREE.Material).dispose();
    }
    this.active.length = 0;
  }

  /**
   * Returns { dodgesThisFrame, hit, nearestDist }. Hit means the run is over.
   * `nearestDist` is the live distance to the closest active projectile —
   * cosmetic (drives the approach rumble), not part of the scoring contract.
   */
  update(
    dt: number,
    t: number,
    shipPos: THREE.Vector2,
  ): { dodgesThisFrame: DodgeEvent[]; hit: boolean; nearestDist: number } {
    const dodges: DodgeEvent[] = [];
    let hit = false;
    let nearestDist = Infinity;

    // Spawn schedule: density ramps from 1.5/s at t=0 to 8/s at t=120s.
    const density = 1.5 + 6.5 * smoothstep(t / 120);
    const spawnPeriod = 1 / density;
    while (t >= this.nextSpawnT) {
      this.spawn(this.nextSpawnT, shipPos);
      this.nextSpawnT += spawnPeriod;
    }

    // Advance projectiles + track approach + cull on exit.
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      p.pos.x += p.dir.x * p.speed * dt;
      p.pos.y += p.dir.y * p.speed * dt;
      p.mesh.position.set(p.pos.x, p.pos.y, 0);

      const dx = p.pos.x - shipPos.x;
      const dy = p.pos.y - shipPos.y;
      const d = Math.hypot(dx, dy);
      if (d < p.nearestApproach) p.nearestApproach = d;
      if (d < nearestDist) nearestDist = d;

      if (!p.hitFlagged && d <= HIT_RADIUS) {
        p.hitFlagged = true;
        hit = true;
        continue;
      }

      // Cull when outside the wide box; emit a dodge event if it qualifies.
      if (
        Math.abs(p.pos.x) > CULL_HALF ||
        Math.abs(p.pos.y) > CULL_HALF
      ) {
        if (p.nearestApproach <= DODGE_RADIUS && !p.hitFlagged) {
          const bucket: 1 | 2 | 3 = p.nearestApproach <= 1.5 ? 3 : p.nearestApproach <= 2.5 ? 2 : 1;
          dodges.push({ idx: p.idx, distance: p.nearestApproach, bucket });
          if (bucket === 3) this.dodgeCounts.tight++;
          else if (bucket === 2) this.dodgeCounts.close++;
          else this.dodgeCounts.clear++;
        }
        this.group.remove(p.mesh);
        p.mesh.geometry.dispose();
        (p.mesh.material as THREE.Material).dispose();
        this.active.splice(i, 1);
      }
    }

    return { dodgesThisFrame: dodges, hit, nearestDist };
  }

  private spawn(spawnT: number, shipPos: THREE.Vector2) {
    // Origin = random point on the perimeter of a 60×60 box (just outside
    // the arena), so projectiles enter visibly rather than popping in.
    const side = Math.floor(this.rng() * 4);
    const u = (this.rng() - 0.5) * 2 * ARENA_HALF; // -20..+20 along the chosen side
    let ox = 0;
    let oy = 0;
    if (side === 0) { ox = -ARENA_HALF - 4; oy = u; }
    else if (side === 1) { ox = ARENA_HALF + 4; oy = u; }
    else if (side === 2) { ox = u; oy = -ARENA_HALF - 4; }
    else { ox = u; oy = ARENA_HALF + 4; }

    // Aim at ship's current position with a small jitter so passes
    // don't all converge identically.
    const tx = shipPos.x + (this.rng() - 0.5) * 6;
    const ty = shipPos.y + (this.rng() - 0.5) * 6;
    const ddx = tx - ox;
    const ddy = ty - oy;
    const dl = Math.hypot(ddx, ddy) || 1;
    const dir = new THREE.Vector2(ddx / dl, ddy / dl);
    const speed = SPEED_MIN + this.rng() * (SPEED_MAX - SPEED_MIN);

    const geo = new THREE.SphereGeometry(0.32, 8, 6);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff5b3a,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(ox, oy, 0);
    this.group.add(mesh);

    this.active.push({
      idx: this.nextIdx++,
      origin: new THREE.Vector2(ox, oy),
      dir,
      speed,
      spawnT,
      pos: new THREE.Vector2(ox, oy),
      mesh,
      nearestApproach: Infinity,
      hitFlagged: false,
    });
  }
}
